import {calculateQuizSessionScore, QuizSession, QuizSessionWithScore} from '@cp/model';
import {useMemo} from 'react';
import subscribeQuizSessionCollectionCompleted from '../../api/quizSession/subscribeQuizSessionCollectionCompleted';
import {useFunctionSubscribe} from '../helper/function/useFunctionSubscribe';
import {useAuth} from '../users/useAuth';

const DEFAULT_LIMIT = 10;

interface UseUserCompletedSessionsOptions {
  limit?: number;
  offset?: number;
}

function mapToSessionWithScore(session: QuizSession): QuizSessionWithScore {
  const score = session.endedAt != null ? calculateQuizSessionScore(session) : 0;
  return {...session, score};
}

export default function useUserCompletedSessions(options?: UseUserCompletedSessionsOptions) {
  const user = useAuth();
  const uid = user?.id ?? '';
  const limit = options?.limit ?? DEFAULT_LIMIT;

  const subscribe = useMemo(
    () => (onData: (data: QuizSessionWithScore[]) => void, onError: (err: Error) => void) =>
      subscribeQuizSessionCollectionCompleted(
        uid,
        (sessions) => {
          onData(sessions.map(mapToSessionWithScore));
        },
        onError,
        limit,
      ),
    [uid, limit],
  );

  const {
    data = [],
    loading,
    error,
  } = useFunctionSubscribe<QuizSessionWithScore[]>(subscribe, {
    skip: !uid,
    deps: [uid, limit],
  });

  return {
    sessions: data,
    loading,
    error,
    refetch: () => Promise.resolve(),
  };
}
